import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from './auth/auth';
import { CompanyInfo, CompanyPermission, CompanyService } from './api/company';

/**
 * Компания текущего пользователя и его права в ней. Разделы кабинета прячутся по правам.
 * Пока данные не загружены (или у пользователя нет компании), права не ограничиваются:
 * ограничение накладывает только бэкенд, а меню лишь убирает недоступное.
 */
@Injectable({ providedIn: 'root' })
export class CompanyContextService {
  private readonly subject = new BehaviorSubject<CompanyInfo | null>(null);
  private loadedForUserId: number | null = null;
  private loading = false;

  readonly company$ = this.subject.asObservable();

  constructor(private auth: AuthService, private companyService: CompanyService) {}

  get company(): CompanyInfo | null {
    return this.subject.value;
  }

  /** Загружает компанию текущего пользователя один раз на пользователя; при смене пользователя сбрасывает. */
  ensureLoaded(): void {
    const user = this.auth.getUser();
    if (!user) {
      this.reset();
      return;
    }
    if (this.loading || this.loadedForUserId === user.id) {
      return;
    }
    const code = user.role?.code;
    if (code !== 'ROLE_FREIGHT_FORWARDER' && code !== 'ROLE_CARGO_OWNER' && code !== 'ROLE_SUPPLIER' && code !== 'ROLE_CUSTOMS_BROKER') {
      this.loadedForUserId = user.id;
      this.subject.next(null);
      return;
    }
    this.loading = true;
    this.companyService.getMyCompany().subscribe({
      next: company => {
        this.loading = false;
        this.loadedForUserId = user.id;
        this.subject.next(company);
      },
      error: () => {
        this.loading = false;
        this.loadedForUserId = user.id;
        this.subject.next(null);
      }
    });
  }

  reset(): void {
    this.loadedForUserId = null;
    this.subject.next(null);
  }

  /** Пункт «Сотрудники» виден администратору компании (право MANAGE_MEMBERS). */
  canManageMembers(): boolean {
    const company = this.subject.value;
    return !!company && company.myPermissions.includes('MANAGE_MEMBERS');
  }

  /** Право есть и компания известна (в отличие от can, без данных о компании возвращает false): для действий только внутри компании. */
  hasPermission(permission: CompanyPermission): boolean {
    const company = this.subject.value;
    return !!company && company.myPermissions.includes(permission);
  }

  /** Есть ли право. Без данных о компании: true (решает бэкенд). */
  can(permission: CompanyPermission): boolean {
    const company = this.subject.value;
    if (!company || !company.myRole) {
      return true;
    }
    return company.myPermissions.includes(permission);
  }
}
